// All formatting is anchored to the scheduling page timezone so the visitor
// always sees the seller's local times, regardless of their own device clock.
// The slot ISO strings carry an offset, so Date parses the absolute instant and
// Intl re-projects it into `timeZone`.

// YYYY-MM-DD for a given Date as seen in `timeZone` (used for availability range
// params and for grouping/keys — never use toISOString here, it would shift days).
export const dateKeyInTimezone = (date, timeZone) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const lookup = type => parts.find(part => part.type === type)?.value;
  return `${lookup('year')}-${lookup('month')}-${lookup('day')}`;
};

// HH:mm of a slot instant, projected into the page timezone.
export const formatSlotTime = (iso, timeZone) =>
  new Intl.DateTimeFormat('pt-BR', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));

// Friendly full date+time for the confirmation screen.
export const formatSlotDateTime = (iso, timeZone) =>
  new Intl.DateTimeFormat('pt-BR', {
    timeZone,
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));

// Builds the next `count` days starting today, each with a stable date key and
// pre-formatted labels for the date picker.
export const buildUpcomingDays = (count, timeZone) => {
  const days = [];
  const now = new Date();
  for (let offset = 0; offset < count; offset += 1) {
    const date = new Date(now.getTime() + offset * 24 * 60 * 60 * 1000);
    const key = dateKeyInTimezone(date, timeZone);
    const weekday = new Intl.DateTimeFormat('pt-BR', {
      timeZone,
      weekday: 'short',
    }).format(date);
    const dayMonth = new Intl.DateTimeFormat('pt-BR', {
      timeZone,
      day: '2-digit',
      month: '2-digit',
    }).format(date);
    days.push({ key, weekday, dayMonth, isToday: offset === 0 });
  }
  return days;
};

// "agosto de 2026" para o cabeçalho do calendário de mês.
export const monthLabel = (year, month) =>
  new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' }).format(
    new Date(Date.UTC(year, month, 1, 12))
  );

// Iniciais dos dias da semana (dom..sáb) para o cabeçalho da grade.
export const weekdayInitials = () => {
  const base = new Date(Date.UTC(2026, 7, 2, 12)); // um domingo
  return Array.from({ length: 7 }, (_, i) =>
    new Intl.DateTimeFormat('pt-BR', { weekday: 'short' })
      .format(new Date(base.getTime() + i * 86400000))
      .replace('.', '')
  );
};

// Células da grade do mês: null nos espaços antes do dia 1, depois
// { key: 'YYYY-MM-DD', day: N } — calendário puro, sem projeção de fuso
// (as keys casam com dateKeyInTimezone porque ambas são datas civis).
export const buildMonthCells = (year, month) => {
  const first = new Date(Date.UTC(year, month, 1, 12));
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0, 12)).getUTCDate();
  const cells = Array.from({ length: first.getUTCDay() }, () => null);
  const pad = n => String(n).padStart(2, '0');
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push({ key: `${year}-${pad(month + 1)}-${pad(day)}`, day });
  }
  return cells;
};

// Soma dias a uma key 'YYYY-MM-DD' (datas civis, sem fuso).
export const addDaysToKey = (key, days) => {
  const [y, m, d] = key.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d + days, 12));
  const pad = n => String(n).padStart(2, '0');
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
};
